import { useMemo } from 'react';

import { cx } from '@libs/classnames';

import { IRematchStatus } from '@features/game/types/fast-game';
import { Button } from '@features/game/ui/PopupControls';
import { components } from '@features/shared/api/game';

import { OnlineGamesModal } from '../OnlineGamesModal';

import styles from './GameFinishedModal.module.css';

type Props = {
  className?: string;
  game: IGame;
  side: 'white' | 'black';
  isMobile?: boolean;
  position?: 'centered' | 'static';
  rematch: IRematchStatus;
  returnText?: string;
  onRematch?: () => void;
  onNewGame?: () => void;
  onAnalysis?: () => void;
  onClose?: () => void;
  onReturn?: () => void;
};

interface IGame {
  result: components['schemas']['GameResult'];
  black: IPlayer;
  white: IPlayer;
  noRatingChange: boolean;
}

interface IPlayer {
  ratingChange?: components['schemas']['PlayerDto']['ratingChange'];
  rating?: components['schemas']['PlayerDto']['rating'];
}

export function GameFinishedModal({
  className,
  game,
  side,
  isMobile = false,
  position,
  rematch,
  returnText,
  onRematch,
  onNewGame,
  onAnalysis,
  onClose,
  onReturn,
}: Props) {
  const player = game[side];
  const isRating = !game.noRatingChange;
  const { result } = game;

  const title = useMemo(() => {
    const isWin =
      (result === 'BlackWin' && side === 'black') || (result === 'WhiteWin' && side === 'white');

    if (isWin) return 'Победа! Игра окончена';

    const isDefeat =
      (result === 'BlackWin' && side === 'white') || (result === 'WhiteWin' && side === 'black');

    if (isDefeat) return 'Поражение. Игра окончена';

    if (result === 'Draw') return 'Ничья. Игра окончена';

    if (result === 'NotPlayed') return 'Игра отменена';

    return 'Игра окончена';
  }, [result, side]);
  return (
    <OnlineGamesModal
      isMobile={isMobile}
      title={title}
      titlePosition="left"
      position={position}
      onClose={onClose}
    >
      <section className={cx(className, styles.finished_modal)}>
        {isRating && (
          <article className={styles.ratings}>
            <p>
              <span>Новый рейтинг</span>
              {(player.rating ?? 0) + (player.ratingChange ?? 0)}
            </p>
            <p>
              <span>Изменение</span>
              {(player.ratingChange ?? 0) > 0
                ? `+${player.ratingChange}`
                : (player.ratingChange ?? 0)}
            </p>
          </article>
        )}

        <div className={styles.buttons}>
          <Button
            className={styles.rematch}
            disabled={rematch === 'player' || rematch === 'none'}
            gold={rematch === 'opponent'}
            onClick={onRematch}
          >
            {rematch === 'available' && 'Предложить реванш'}
            {rematch === 'none' && 'Реванш недоступен'}
            {rematch === 'player' && 'Ожидание...'}
            {rematch === 'opponent' && 'Соперник предлагает реванш'}
          </Button>
          {onNewGame && <Button onClick={onNewGame}>Новый соперник</Button>}

          <Button onClick={onAnalysis} disabled={!onAnalysis}>
            Анализировать партию
          </Button>

          {onReturn && <Button onClick={onReturn}>{returnText ?? 'Вернуться'}</Button>}
        </div>
      </section>
    </OnlineGamesModal>
  );
}
